import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dropdown,
  DropDownButton,
  type DropdownPlacement,
} from '@/shared/components/Dropdown';
import { listOfferApiService } from '@/epics/offerManagment/api/listOfferApiService';
import { viewOffer } from '@/epics/offerManagment/api/viewOfferApiService';
import { MenuItemProps } from '@/shared/components/Menu/types';
import type { OfferListItem } from '@/epics/offerManagment/types/offerListType';
import { useSearchLoading } from '@/shared/hooks/useSearchLoading';

interface OfferInfiniteDropdownProps {
  value: string;
  onChange: (value: string, offer?: OfferListItem) => void;
  disabled?: boolean;
  hideChevron?: boolean;
  label?: React.ReactNode;
  placement?: DropdownPlacement;
  /**
   * Display name of the already-selected offer.
   * Shown immediately on render while the API fetches the full list,
   * so the dropdown trigger never blanks out during loading.
   */
  displayName?: string;
}

/** "OfferName (20% off)" / "OfferName (50 zł off)" — used for both the list options and the selected value. */
function formatOfferOption(
  offerName: string,
  discountType: string,
  discountValue: number
): string {
  const discountLabel =
    discountType === 'PERCENTAGE'
      ? `${discountValue}% off`
      : `${discountValue} zł off`;
  return `${offerName} (${discountLabel})`;
}

export const OfferInfiniteDropdown: React.FC<OfferInfiniteDropdownProps> = ({
  value,
  onChange,
  disabled,
  hideChevron,
  label = 'Oferta',
  placement,
  displayName,
}) => {
  const [offers, setOffers] = useState<OfferListItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [resolvedOffer, setResolvedOffer] = useState<{
    offerName: string;
    discountType: string;
    discountValue: number;
  } | null>(null);
  const [resolving, setResolving] = useState(false);
  // Separate from `loading` — pagination must never share it with search.
  const [loadingMore, setLoadingMore] = useState(false);
  const {
    search: searchQuery,
    setSearch: setSearchQuery,
    loading,
    setLoading,
  } = useSearchLoading('');

  // Own debounce tick, so a settle back to an unchanged value (e.g. type one
  // letter, delete it) still fires and resets `loading` — fixes it getting
  // stuck true when useSearchLoading's internal debounce bails out as a no-op.
  const [searchVersion, setSearchVersion] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setSearchVersion((v) => v + 1), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch selected offer by ID when it's not yet in the paginated list (e.g. edit mode, offer is page 10+)
  useEffect(() => {
    if (!value) {
      setResolvedOffer(null);
      setResolving(false);
      return;
    }
    let mounted = true;
    setResolving(true);
    viewOffer({ offerId: value }).then((result) => {
      if (mounted && result.success && result.data) {
        setResolvedOffer({
          offerName: result.data.offerName || value,
          discountType: result.data.discountType,
          discountValue: result.data.discountValue,
        });
      }
      if (mounted) {
        setResolving(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, [value]);

  const mountedRef = useRef(true);
  useEffect(() => {
    // Reset on mount too — StrictMode double-invokes (mount/cleanup/mount).
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchFirstPage = useCallback(
    async (search: string) => {
      setLoading(true);
      try {
        const response = await listOfferApiService.listOffers({
          page: 1,
          limit: 10,
          ...(search ? { search } : {}),
        });
        if (!mountedRef.current) {
          return;
        }
        setOffers(response.data);
        setHasMore(response.hasNextPage);
        setPage(1);
      } catch (error) {
        console.error('Error fetching offers', error);
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [setLoading]
  );

  // Re-runs (replacing the list) on every debounce tick, since search is
  // server-side.
  useEffect(() => {
    fetchFirstPage(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVersion]);

  // Also re-fetch on every reopen — otherwise an offer created elsewhere
  // while this page stays open never appears until a full page reload.
  useEffect(() => {
    if (isDropdownOpen) {
      fetchFirstPage(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDropdownOpen]);

  const fetchMore = useCallback(async () => {
    if (loadingMore || loading || !hasMore) {
      return;
    }
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const response = await listOfferApiService.listOffers({
        page: nextPage,
        limit: 10,
        ...(searchQuery ? { search: searchQuery } : {}),
      });
      setOffers((prev) => {
        const newItems = response.data.filter(
          (newItem) => !prev.some((item) => item.id === newItem.id)
        );
        return [...prev, ...newItems];
      });
      setHasMore(response.hasNextPage);
      setPage(response.page);
    } catch (error) {
      console.error('Error fetching more offers', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, loading, hasMore, page, searchQuery]);

  const offerItems: MenuItemProps[] = offers.map((offer) => ({
    id: offer.id,
    label: formatOfferOption(
      offer.offerName || offer.id,
      offer.discountType,
      offer.discountValue
    ),
  }));

  // While a search-driven fetch is in flight, the list is replaced outright
  // by the spinner (not appended to stale results) — this covers typing AND
  // clearing the search, since both go through `loading`. Appending is
  // reserved for plain scroll-triggered pagination (`loadingMore`).
  const items: MenuItemProps[] = loading
    ? [{ id: '__loading__', label: 'Ładowanie...', disabled: true }]
    : loadingMore
      ? [
          ...offerItems,
          { id: '__loading__', label: 'Ładowanie...', disabled: true },
        ]
      : searchQuery && offers.length === 0
        ? [
            {
              id: '__no_results__',
              label: 'Nie znaleziono wyników',
              disabled: true,
            },
          ]
        : resolving
          ? [
              { id: '__loading__', label: 'Ładowanie...', disabled: true },
              ...offerItems,
            ]
          : offerItems;

  const selectedOffer = offers.find((o) => o.id === value);
  // Use the pre-provided displayName immediately (while API is fetching) so the
  // dropdown trigger never blanks out. Once the list arrives or the API completes,
  // prefer the actual data. If displayName is available but no discount yet, just show
  // the name (the discount will be filled in once resolvedOffer completes).
  const displayValue = selectedOffer
    ? formatOfferOption(
        selectedOffer.offerName || selectedOffer.id,
        selectedOffer.discountType,
        selectedOffer.discountValue
      )
    : resolvedOffer
      ? formatOfferOption(
          resolvedOffer.offerName,
          resolvedOffer.discountType,
          resolvedOffer.discountValue
        )
      : displayName || (value ? `ID: ${value}` : '');

  return (
    <Dropdown
      menuFullWidth
      menuMaxVisibleItems={7}
      {...(placement ? { placement } : {})}
      open={isDropdownOpen}
      onOpenChange={(open) => {
        setIsDropdownOpen(open);
        if (!open) {
          setSearchQuery('');
        }
      }}
      items={items}
      selectedId={value}
      showSearch
      searchPlaceholder="Szukaj oferty..."
      onSearchChange={setSearchQuery}
      onItemClick={(id) => {
        if (id !== '__loading__' && id !== '__no_results__') {
          onChange(
            id,
            offers.find((o) => o.id === id)
          );
          setIsDropdownOpen(false);
        }
      }}
      toolTip={false}
      trigger={({ open, toggle, disabled: dropDisabled, error }) => (
        <DropDownButton
          data-testid="offer-dropdown"
          label={label}
          value={displayValue}
          open={open}
          toggle={toggle}
          disabled={Boolean(disabled || dropDisabled)}
          error={error}
          toolTip={false}
          hideChevron={hideChevron ?? false}
        />
      )}
      disabled={disabled ?? false}
      onScrollEnd={() => {
        if (hasMore && !loading && !loadingMore) {
          fetchMore();
        }
      }}
    />
  );
};
